// src/RecipeNotesManager.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Card, CardContent, Typography, Accordion, AccordionSummary, AccordionDetails,
  TextField, Button, Snackbar, Alert, Stack, Switch, FormControlLabel, LinearProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip, Divider,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ImageIcon from "@mui/icons-material/Image";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import MDEditor from "@uiw/react-md-editor";
import {
  getFirestore, collection, getDocs, doc, updateDoc, setDoc, deleteDoc
} from "firebase/firestore";
import {
  getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
// ✅ use modern maintained DnD (drop-in replacement)
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

/** Image shape on each note: { id, url, path, name?, alt?, created_at } */

function RecipeNotesManager() {
  const db = getFirestore();
  const storage = getStorage();

  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [notes, setNotes] = useState([]);
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, noteId: null });

  // upload state per note
  const [uploadProgress, setUploadProgress] = useState({}); // { [noteId]: 0..100 }
  const [uploadBusy, setUploadBusy] = useState({});         // { [noteId]: boolean }
  const fileInputsRef = useRef({});                         // hidden <input type="file" multiple> per note
  const noteTextareaRefs = useRef({});                      // noteId -> DOM <textarea> we capture onFocus

  // ---------------- utils ----------------
  const sanitizeFileName = (name) =>
    name.toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/-+/g, "-");

  const normalizeName = (s) =>
    (s || "")
      .toLowerCase()
      .trim()
      .replace(/\.(png|jpe?g|gif|webp|svg)$/i, "")
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 64);

  const guessPathFromUrlSafe = (urlStr) => {
    try {
      const url = new URL(urlStr);
      // works for both firebasestorage.googleapis.com and firebasestorage.app
      return decodeURIComponent(url.pathname.replace(/^\/v0\/b\/[^/]+\/o\//, ""));
    } catch {
      return "";
    }
  };

  // robust Storage ref builder
  const getRefForImage = (storage, img) => {
    try {
      if (img?.path) return storageRef(storage, img.path);
      const p = guessPathFromUrlSafe(img?.url || "");
      if (p) return storageRef(storage, p);
    } catch {}
    return null;
  };

  const isNameTaken = (noteId, name, exceptImageId = null) => {
    const note = notes.find(n => n.id === noteId);
    if (!note?.images) return false;
    return note.images.some(img => img.name && img.name === name && img.id !== exceptImageId);
  };

  const ensureUniqueName = (noteId, base) => {
    let name = base || "image";
    if (!isNameTaken(noteId, name)) return name;
    let i = 2;
    while (isNameTaken(noteId, `${name}-${i}`)) i++;
    return `${name}-${i}`;
  };

  const REF_START = "<!-- image-refs -->";
  const REF_END = "<!-- /image-refs -->";

  const upsertImageRefBlock = (content, name, url) => {
    const start = content.indexOf(REF_START);
    const end = content.indexOf(REF_END);
    let before = content;
    let after = "";
    let lines = [];

    if (start !== -1 && end !== -1 && end > start) {
      before = content.slice(0, start);
      const between = content.slice(start + REF_START.length, end);
      after = content.slice(end + REF_END.length);
      lines = between
        .split("\n")
        .map(l => l.trim())
        .filter(l => l.length > 0);
    }

    const def = `[${name}]: ${url}`;
    let replaced = false;
    lines = lines.map((l) => {
      if (l.startsWith(`[${name}]`)) {
        replaced = true;
        return def;
      }
      return l;
    });
    if (!replaced) lines.push(def);

    const block = `\n${REF_START}\n${lines.join("\n")}\n${REF_END}\n`;
    const beforeTrim = before.replace(/\s*$/, "");
    const afterTrim = after ? `\n${after.replace(/^\n*/, "")}` : "";

    return `${beforeTrim}\n\n${block}${afterTrim}`;
  };

  // ---------------- data load ----------------
  const fetchNotes = async () => {
    setLoading(true);
    const colRef = collection(db, "recipe_notes");
    const snapshot = await getDocs(colRef);
    const results = snapshot.docs.map((docSnap, idx) => {
      const data = docSnap.data();
      let images = Array.isArray(data.images) ? data.images : [];
      // migrate legacy image_url for display
      if (!images?.length && data.image_url) {
        images = [{
          id: uuidv4(),
          url: data.image_url,
          path: guessPathFromUrlSafe(data.image_url),
          name: "cover",
          created_at: new Date().toISOString(),
        }];
      }
      return {
        id: docSnap.id,
        sort_order: idx,
        ...data,
        images,
        isNew: false,
      };
    });
    setNotes(results.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    setLoading(false);

    // persist migration
    for (const n of results) {
      if ((!n.images || n.images.length === 0) && n.image_url) {
        const migrated = [{
          id: uuidv4(),
          url: n.image_url,
          path: guessPathFromUrlSafe(n.image_url),
          name: "cover",
          created_at: new Date().toISOString(),
        }];
        updateDoc(doc(db, "recipe_notes", n.id), {
          images: migrated,
          updated_at: new Date().toISOString(),
        }).catch(() => {});
      }
    }
  };

  useEffect(() => { fetchNotes(); }, []);

  // ---------------- note actions ----------------
  const handleSave = async (id) => {
    const existing = notes.find(n => n.id === id);
    const ref = doc(db, "recipe_notes", id);
    const legacyCover = existing.images?.[0]?.url || existing.image_url || "";
    await setDoc(ref, {
      ...existing,
      image_url: legacyCover,
      updated_at: new Date().toISOString(),
      version: (existing.version || 0) + 1,
      isNew: false,
    });
    setNotes((prev) =>
      prev.map((note) =>
        note.id === id
          ? { ...note, version: (note.version || 0) + 1, updated_at: new Date().toISOString(), isNew: false }
          : note
      )
    );
    setSnackbar({ open: true, message: `Saved ${existing.title}`, severity: "success" });
  };

  const confirmAction = (action, noteId) => setConfirmDialog({ open: true, action, noteId });

  const handleConfirmedAction = async () => {
    const { action, noteId } = confirmDialog;
    setConfirmDialog({ open: false, action: null, noteId: null });
    const ref = doc(db, "recipe_notes", noteId);

    if (action === "archive") {
      await updateDoc(ref, { archived: true });
      setNotes((prev) => prev.map((n) => n.id === noteId ? { ...n, archived: true } : n));
      setSnackbar({ open: true, message: "Note archived", severity: "info" });
    } else if (action === "delete") {
      const note = notes.find(n => n.id === noteId);
      const all = (note?.images || []).slice();
      if (note?.image_url && !all.some(img => img.url === note.image_url)) {
        all.push({ url: note.image_url, path: guessPathFromUrlSafe(note.image_url) });
      }
      for (const img of all) {
        const delRef = getRefForImage(storage, img);
        if (!delRef) continue;
        try {
          await deleteObject(delRef);
        } catch (err) {
          if (err?.code !== "storage/object-not-found") {
            console.error("delete note images:", err);
          }
        }
      }
      await deleteDoc(ref);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      setSnackbar({ open: true, message: "Note deleted", severity: "error" });
    } else if (action === "restore") {
      await updateDoc(ref, { archived: false });
      setNotes((prev) => prev.map((n) => n.id === noteId ? { ...n, archived: false } : n));
      setSnackbar({ open: true, message: "Note restored", severity: "success" });
    }
  };

  const handleAddNote = () => {
    const newId = uuidv4();
    const newNote = {
      id: newId,
      title: "New Note",
      content: "",
      images: [],
      image_url: "",
      updated_at: new Date().toISOString(),
      version: 1,
      archived: false,
      isNew: true,
    };
    setNotes((prev) => [...prev, newNote]);
    setExpandedNoteId(newId);
    setSnackbar({ open: true, message: "New note created", severity: "success" });
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(notes);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    const updates = reordered.map((note, index) => ({ ...note, sort_order: index }));
    setNotes(updates);
    for (const note of updates) await updateDoc(doc(db, "recipe_notes", note.id), { sort_order: note.sort_order });
    setSnackbar({ open: true, message: "Note order updated", severity: "success" });
  };

  const handleChange = (id, field, value) => {
    setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, [field]: value } : n)));
  };

  // --- textarea access (no ref warning) ---
  const getTextareaForNote = (noteId) => {
    const cached = noteTextareaRefs.current[noteId];
    if (cached && typeof cached.selectionStart === "number") return cached;
    const wrap = document.getElementById(`note-editor-${noteId}`);
    const ta = wrap ? wrap.querySelector("textarea") : null;
    if (ta) noteTextareaRefs.current[noteId] = ta;
    return ta;
  };

  // ---------------- insert helpers ----------------
  const insertImageInlineAtCursor = (noteId, img) => {
    const note = notes.find(n => n.id === noteId);
    const content = note?.content || "";
    const ta = getTextareaForNote(noteId);
    const altBase = img.name || note?.title || "image";

    if (!ta || typeof ta.selectionStart !== "number" || typeof ta.selectionEnd !== "number") {
      const md = `\n\n![${altBase}](${img.url})\n`;
      handleChange(noteId, "content", content + md);
      setSnackbar({ open: true, message: "Image inserted", severity: "success" });
      return;
    }

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = content.slice(start, end).replace(/\n/g, " ").trim();
    const alt = selected || altBase;
    const md = `![${alt}](${img.url})`;
    const newVal = content.slice(0, start) + md + content.slice(end);
    handleChange(noteId, "content", newVal);
    requestAnimationFrame(() => {
      try {
        ta.focus();
        const pos = start + md.length;
        ta.setSelectionRange(pos, pos);
      } catch {}
    });
    setSnackbar({ open: true, message: "Image inserted at cursor", severity: "success" });
  };

  const insertImageByName = (noteId, img) => {
    const note = notes.find(n => n.id === noteId);
    const name = img.name || "image";
    const alt = name;
    const ta = getTextareaForNote(noteId);
    const current = note?.content || "";

    const md = `![${alt}][${name}]`;
    let newVal;
    if (!ta || typeof ta.selectionStart !== "number" || typeof ta.selectionEnd !== "number") {
      newVal = current + `\n\n${md}\n`;
    } else {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      newVal = current.slice(0, start) + md + current.slice(end);
    }
    newVal = upsertImageRefBlock(newVal, name, img.url);
    handleChange(noteId, "content", newVal);

    requestAnimationFrame(() => {
      if (ta) {
        try {
          ta.focus();
          const pos = (note?.content || "").length + md.length;
          ta.setSelectionRange(pos, pos);
        } catch {}
      }
    });

    setSnackbar({ open: true, message: "Inserted reference and updated definitions", severity: "success" });
  };

  const copyImageMarkdown = async (img, mode = "inline") => {
    const name = img.name || "image";
    const snippet = mode === "inline" ? `![${name}](${img.url})` : `![${name}][${name}]`;
    try {
      await navigator.clipboard.writeText(snippet);
      setSnackbar({ open: true, message: "Markdown copied", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Couldn’t copy to clipboard", severity: "warning" });
    }
  };

  // ---------------- image upload / edit ----------------
  const handleImagesUpload = (noteId, filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;

    setUploadBusy((p) => ({ ...p, [noteId]: true }));
    setUploadProgress((p) => ({ ...p, [noteId]: 0 }));

    let completedBytes = 0;
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0) || 1;

    files.forEach((file, idx) => {
      const base = file.name.replace(/\.[^.]+$/, "");
      const defaultName = ensureUniqueName(noteId, normalizeName(base) || `image-${Date.now()}-${idx + 1}`);
      const safeName = `${Date.now()}-${sanitizeFileName(file.name)}`;
      const path = `recipe_notes/${noteId}/${safeName}`;
      const ref = storageRef(storage, path);

      const task = uploadBytesResumable(ref, file, { contentType: file.type || "application/octet-stream" });

      task.on(
        "state_changed",
        (snap) => {
          const current = completedBytes + snap.bytesTransferred;
          const pct = Math.round((current / totalBytes) * 100);
          setUploadProgress((prev) => ({ ...prev, [noteId]: Math.min(100, Math.max(0, pct)) }));
        },
        (err) => {
          setSnackbar({ open: true, message: `Upload failed: ${err.message}`, severity: "error" });
        },
        async () => {
          completedBytes += file.size;
          const url = await getDownloadURL(task.snapshot.ref);
          const newImg = { id: uuidv4(), url, path, name: defaultName, created_at: new Date().toISOString() };

          setNotes((prev) =>
            prev.map((n) => (n.id === noteId ? { ...n, images: [...(n.images || []), newImg] } : n))
          );

          try {
            const currentImages = (notes.find(n => n.id === noteId)?.images || []).concat(newImg);
            await updateDoc(doc(db, "recipe_notes", noteId), {
              images: currentImages,
              updated_at: new Date().toISOString(),
            });
          } catch {}

          if (completedBytes >= totalBytes) {
            setUploadBusy((p) => ({ ...p, [noteId]: false }));
            setUploadProgress((p) => ({ ...p, [noteId]: 100 }));
            setTimeout(() => setUploadProgress((p) => ({ ...p, [noteId]: 0 })), 600);
            setSnackbar({ open: true, message: "Images uploaded", severity: "success" });
          }
        }
      );
    });
  };

  const handleRemoveImage = async (noteId, imageId) => {
    const note = notes.find(n => n.id === noteId);
    const img = note?.images?.find(i => i.id === imageId);
    if (!img) return;

    try {
      const delRef = getRefForImage(storage, img);
      if (delRef) await deleteObject(delRef);
    } catch (err) {
      if (err?.code !== "storage/object-not-found") {
        console.error("delete image:", err);
        setSnackbar({ open: true, message: `Couldn't delete file: ${err.message}`, severity: "warning" });
      }
    }

    const updated = (note.images || []).filter(i => i.id !== imageId);
    handleChange(noteId, "images", updated);
    updateDoc(doc(db, "recipe_notes", noteId), {
      images: updated,
      updated_at: new Date().toISOString(),
    }).catch(() => {});
    setSnackbar({ open: true, message: "Image removed", severity: "info" });
  };

  const moveImage = (noteId, index, dir) => {
    const note = notes.find(n => n.id === noteId);
    if (!note?.images || index < 0) return;
    const next = index + dir;
    if (next < 0 || next >= note.images.length) return;
    const arr = [...note.images];
    [arr[index], arr[next]] = [arr[next], arr[index]];
    handleChange(noteId, "images", arr);
    updateDoc(doc(db, "recipe_notes", noteId), { images: arr, updated_at: new Date().toISOString() }).catch(() => {});
  };

  const handleRenameImage = (noteId, imageId, raw) => {
    const newNameRaw = normalizeName(raw);
    if (!newNameRaw) return;

    if (isNameTaken(noteId, newNameRaw, imageId)) {
      setSnackbar({ open: true, message: `Name "${newNameRaw}" already used in this note.`, severity: "warning" });
      return;
    }
    setNotes((prev) =>
      prev.map((n) => {
        if (n.id !== noteId) return n;
        const imgs = (n.images || []).map((img) => img.id === imageId ? { ...img, name: newNameRaw } : img);
        return { ...n, images: imgs };
      })
    );
  };

  const persistRenameImage = (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;
    updateDoc(doc(db, "recipe_notes", noteId), { images: note.images, updated_at: new Date().toISOString() })
      .then(() => setSnackbar({ open: true, message: "Image name saved", severity: "success" }))
      .catch(() => {});
  };

  // ---------------- render ----------------
  const filteredNotes = notes.filter((n) => n.archived === showArchived);

  return (
    <Card>
      {loading && <LinearProgress />}
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">📝 Recipe Notes Manager</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={showArchived} onChange={() => setShowArchived(!showArchived)} />}
              label="Show Archived"
            />
            <Button variant="contained" onClick={handleAddNote}>New Note</Button>
          </Stack>
        </Stack>

        {filteredNotes.length === 0 && (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {showArchived ? "No archived notes." : "No active notes. Click 'New Note' to get started."}
          </Typography>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="notes">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {filteredNotes.map((note, index) => (
                  <Draggable draggableId={note.id} index={index} key={note.id} isDragDisabled={note.archived}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          boxShadow: snapshot.isDragging ? "0 2px 10px rgba(0,0,0,0.2)" : undefined,
                          ...provided.draggableProps.style
                        }}
                      >
                        <Accordion
                          expanded={expandedNoteId === note.id}
                          onChange={() => setExpandedNoteId(expandedNoteId === note.id ? null : note.id)}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            {...provided.dragHandleProps}
                            sx={{ cursor: note.archived ? "default" : "grab", display: "flex", alignItems: "center", gap: 1 }}
                          >
                            <DragIndicatorIcon fontSize="small" sx={{ opacity: note.archived ? 0.3 : 0.6 }} />
                            <Typography variant="subtitle1">{note.title || note.id}</Typography>
                          </AccordionSummary>

                          <AccordionDetails>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Version: {note.version || "N/A"} | Last Updated: {note.updated_at ? new Date(note.updated_at).toLocaleString() : "Invalid Date"}
                            </Typography>

                            <TextField
                              label="Title"
                              fullWidth
                              value={note.title}
                              onChange={(e) => handleChange(note.id, "title", e.target.value)}
                              margin="normal"
                              disabled={note.archived}
                            />

                            {/* Note Images (multi + names) */}
                            <Stack direction="column" spacing={1} sx={{ mt: 1, mb: 2 }} data-testid="note-images-section">
                              <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <ImageIcon fontSize="small" /> Note Images
                              </Typography>

                              {/* Preview grid */}
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {(note.images || []).map((img, i) => (
                                  <Stack key={img.id} spacing={0.5} sx={{ border: "1px solid rgba(0,0,0,0.12)", p: 0.75, borderRadius: 1, width: 200 }}>
                                    <img
                                      src={img.url}
                                      alt={`${img.name || note.title || "image"} ${i + 1}`}
                                      style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 4 }}
                                    />

                                    {/* name editor */}
                                    <TextField
                                      size="small"
                                      label="Name"
                                      value={img.name || ""}
                                      onChange={(e) => handleRenameImage(note.id, img.id, e.target.value)}
                                      onBlur={() => persistRenameImage(note.id)}
                                      placeholder="e.g. creamer-chart"
                                      disabled={note.archived}
                                      helperText="Use unique, lowercase names"
                                    />

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                      <Stack direction="row" spacing={0.5}>
                                        <IconButton size="small" onClick={() => moveImage(note.id, i, -1)} disabled={i === 0}>
                                          <ArrowBackIosNewIcon fontSize="inherit" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => moveImage(note.id, i, +1)} disabled={i === (note.images?.length || 0) - 1}>
                                          <ArrowForwardIosIcon fontSize="inherit" />
                                        </IconButton>
                                      </Stack>

                                      <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Tooltip title="Insert at cursor (inline)">
                                          <span>
                                            <IconButton
                                              size="small"
                                              disabled={note.archived}
                                              onClick={() => insertImageInlineAtCursor(note.id, img)}
                                            >
                                              <AddPhotoAlternateIcon fontSize="inherit" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>

                                        <Tooltip title="Insert by name (adds reference)">
                                          <span>
                                            <IconButton
                                              size="small"
                                              disabled={note.archived || !img.name}
                                              onClick={() => insertImageByName(note.id, img)}
                                            >
                                              <LinkIcon fontSize="inherit" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>

                                        <Tooltip title="Copy Markdown (inline)">
                                          <span>
                                            <IconButton
                                              size="small"
                                              disabled={note.archived}
                                              onClick={() => copyImageMarkdown(img, "inline")}
                                            >
                                              <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>

                                        <Tooltip title="Copy Markdown (reference)">
                                          <span>
                                            <IconButton
                                              size="small"
                                              disabled={note.archived || !img.name}
                                              onClick={() => copyImageMarkdown(img, "reference")}
                                            >
                                              <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>

                                        <Tooltip title="Remove">
                                          <span>
                                            <IconButton size="small" color="error" onClick={() => handleRemoveImage(note.id, img.id)}>
                                              <DeleteIcon fontSize="inherit" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </Stack>
                                    </Stack>
                                  </Stack>
                                ))}

                                {!note.images?.length && (
                                  <Typography color="text.secondary">No images attached.</Typography>
                                )}
                              </Stack>

                              {/* Upload / progress */}
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CloudUploadIcon />}
                                  disabled={!!uploadBusy[note.id] || note.archived}
                                  onClick={() => fileInputsRef.current[note.id]?.click()}
                                >
                                  {note.images?.length ? "Add Images" : "Upload Images"}
                                </Button>
                                {!!uploadBusy[note.id] && (
                                  <LinearProgress
                                    variant="determinate"
                                    value={uploadProgress[note.id] || 0}
                                    sx={{ minWidth: 180 }}
                                  />
                                )}
                              </Stack>

                              {/* Hidden multiple file input */}
                              <input
                                ref={(el) => (fileInputsRef.current[note.id] = el)}
                                type="file"
                                accept="image/*"
                                multiple
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const files = e.target.files;
                                  if (files?.length) handleImagesUpload(note.id, files);
                                  e.target.value = ""; // reset
                                }}
                              />
                            </Stack>

                            <Divider sx={{ my: 1 }} />

                            {/* Give the editor a stable wrapper id and capture textarea onFocus */}
                            <div data-color-mode="light" id={`note-editor-${note.id}`}>
                              <MDEditor
                                value={note.content || ""}
                                onChange={(val) => handleChange(note.id, "content", val)}
                                height={240}
                                preview={note.archived ? "preview" : "edit"}
                                textareaProps={{
                                  onFocus: (e) => { noteTextareaRefs.current[note.id] = e.target; },
                                  placeholder: "Write your note…"
                                }}
                              />
                            </div>

                            {!note.archived ? (
                              <Stack direction="row" spacing={2} mt={2}>
                                <Button variant="contained" onClick={() => handleSave(note.id)} disabled={!!uploadBusy[note.id]}>
                                  Save
                                </Button>
                                <Button variant="outlined" color="warning" onClick={() => confirmAction("archive", note.id)} disabled={!!uploadBusy[note.id]}>
                                  Archive
                                </Button>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={2} mt={2}>
                                <Button variant="contained" color="primary" onClick={() => confirmAction("restore", note.id)}>Restore</Button>
                                <Button variant="outlined" color="error" onClick={() => confirmAction("delete", note.id)}>Delete Permanently</Button>
                              </Stack>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, noteId: null })}>
          <DialogTitle>Are you sure?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmDialog.action === "archive"
                ? `This will archive the note: "${notes.find(n => n.id === confirmDialog.noteId)?.title || 'Untitled'}".`
                : confirmDialog.action === "restore"
                ? `This will restore the note: "${notes.find(n => n.id === confirmDialog.noteId)?.title || 'Untitled'}".`
                : `This will permanently delete the note: "${notes.find(n => n.id === confirmDialog.noteId)?.title || 'Untitled'}". This cannot be undone.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog({ open: false, action: null, noteId: null })}>Cancel</Button>
            <Button onClick={handleConfirmedAction} color={confirmDialog.action === "delete" ? "error" : "warning"} autoFocus>
              {confirmDialog.action === "delete" ? "Delete Permanently" : confirmDialog.action === "restore" ? "Restore" : "Archive"}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default RecipeNotesManager;
